"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import type { ErrorDetail } from "@/server/narmesteLederErrorUtils";
export type SubmitMockScenario = "submit-error" | "slow-submit";
const DEFAULT_ERROR: ErrorDetail = {
  title: "Beklager! Det har oppstått en uventet feil",
  message: "Vi klarte ikke å gjennomføre handlingen. Prøv igjen senere."
};
const getMockScenario = (): SubmitMockScenario | undefined => {
  if (typeof window === "undefined") return undefined;
  const params = new URLSearchParams(window.location.search);
  return params.get("mockScenario") as SubmitMockScenario | undefined;
};

/**
 * Generic mock hook factory for submit actions.
 * Control behavior via URL query param: ?mockScenario=submit-error or ?mockScenario=slow-submit
 *
 * SAFETY: This mock is only used in local/demo environments.
 * In production, the real hook is used and this code is never executed.
 */
export const useMockSubmitAction = () => {
  const $ = _c(4);
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = async (...t1) => {
      const args = t1;
      const options = args[args.length - 1] as {
        onSuccess?: () => void;
      } | undefined;
      setIsPending(true);
      setError(null);
      const mockScenario = getMockScenario();
      const delay = mockScenario === "slow-submit" ? 2000 : 600;
      await new Promise(resolve => setTimeout(resolve, delay));
      setIsPending(false);
      if (mockScenario === "submit-error") {
        setError(DEFAULT_ERROR);
      } else {
        options?.onSuccess?.();
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const action = t0;
  let t1;
  if ($[1] !== error || $[2] !== isPending) {
    t1 = {
      action,
      isPending,
      error
    };
    $[1] = error;
    $[2] = isPending;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1 as const;
};